import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Truck, Search, RotateCcw, FileSpreadsheet, Loader2, ArrowUp, ArrowDown, ArrowUpDown, RefreshCw,
  ListChecks, FileText, Users, Sigma, IndianRupee, AlarmClock, ChevronRight, ChevronDown,
} from "lucide-react";
import { fmtDate, fmtQty } from "@/lib/dcGinReportExport";
import { formatCurrencyTotals, formatMoney } from "@/lib/currency";

// Pending Sales Order Report — Reports → Pending Sales Order Report.
// Read-only: one row per Sales Order line whose material is not yet fully
// dispatched (or dispatched but not yet invoiced), computed live by the
// backend from Sales Orders, Packing Slips, Delivery Challans and Tax
// Invoices (GET /api/reports/pending-sales-orders, see
// backend/pending_so_report.py). Nothing is entered here — the page refetches
// every minute and on focus, so a line drops off as soon as it's dispatched
// and invoiced on those screens.

const REFRESH_MS = 60_000;

export const PSO_COLUMNS = [
  { field: "so_code", header: "SO No." },
  { field: "so_date", header: "SO Date", kind: "date" },
  { field: "customer_name", header: "Customer" },
  { field: "part_code", header: "Part No." },
  { field: "part_name", header: "Description" },
  { field: "delivery_date", header: "Due Date", kind: "date" },
  { field: "ordered_qty", header: "Ordered", kind: "number" },
  { field: "packed_qty", header: "Packed", kind: "number" },
  { field: "dc_ready_qty", header: "DC Ready", kind: "number" },
  { field: "dispatched_qty", header: "Dispatched", kind: "number" },
  { field: "invoiced_qty", header: "Invoiced", kind: "number" },
  { field: "balance_qty", header: "Balance", kind: "number" },
  { field: "pending_value", header: "Pending Value", kind: "money" },
  { field: "fg_stock_qty", header: "FG Stock", kind: "number" },
  { field: "stage", header: "Stage" },
  { field: "reasons", header: "Pending Reasons" },
  { field: "age_days", header: "Age (Days)", kind: "number" },
];

// Extra columns that only go to Excel.
const EXPORT_EXTRA = [
  { field: "to_code", header: "TO No." },
  { field: "po_reference", header: "Customer PO" },
  { field: "sales_person", header: "Sales Person" },
  { field: "uom", header: "UOM" },
  { field: "currency", header: "Currency" },
  { field: "rate", header: "Net Rate", kind: "number" },
  { field: "not_on_dc_qty", header: "Not on any DC", kind: "number" },
  { field: "uninvoiced_qty", header: "DC Qty Not Invoiced", kind: "number" },
  { field: "overdue_days", header: "Overdue (Days)", kind: "number" },
  { field: "packing_slip_codes", header: "Packing Slips" },
  { field: "dc_codes", header: "DCs" },
  { field: "invoice_codes", header: "Invoices" },
];

const SORTABLE = new Set([
  "so_date", "so_code", "customer_name", "part_code", "delivery_date", "ordered_qty",
  "dispatched_qty", "balance_qty", "pending_value", "age_days", "stage",
]);

const STAGE_TONE = {
  "Not Packed – No DC": "bg-rose-50 text-rose-700",
  "Packed – DC Pending": "bg-amber-50 text-amber-700",
  "DC Raised – Dispatch Pending": "bg-blue-50 text-blue-700",
  "Partially Dispatched": "bg-violet-50 text-violet-700",
  "Dispatched – Invoice Pending": "bg-emerald-50 text-emerald-700",
};
const DEFAULT_STAGES = Object.keys(STAGE_TONE);

const SUMMARY = [
  { key: "total_lines", label: "Pending Lines", icon: ListChecks, tone: "text-amber-700 bg-amber-50" },
  { key: "total_orders", label: "Sales Orders", icon: FileText, tone: "text-blue-700 bg-blue-50" },
  { key: "total_customers", label: "Customers", icon: Users, tone: "text-slate-700 bg-slate-100" },
  { key: "balance_qty", label: "Balance Qty", icon: Sigma, tone: "text-violet-700 bg-violet-50" },
  { key: "pending_value", label: "Pending Value", icon: IndianRupee, tone: "text-emerald-700 bg-emerald-50", money: true },
  { key: "overdue_lines", label: "Overdue Lines", icon: AlarmClock, tone: "text-red-700 bg-red-50" },
];

const EMPTY_FILTERS = {
  date_from: "", date_to: "", due_from: "", due_to: "", customer: "", so_no: "", item: "",
  sales_person: "", stage: "all", reason: "all", overdue_only: false, search: "",
};

const PAGE_SIZES = [25, 50, 100, 250];

function toParams(f) {
  const p = {};
  for (const k of ["date_from", "date_to", "due_from", "due_to", "customer", "so_no", "item", "sales_person", "search"]) {
    if (f[k] && String(f[k]).trim()) p[k] = String(f[k]).trim();
  }
  if (f.stage && f.stage !== "all") p.stage = f.stage;
  if (f.reason && f.reason !== "all") p.reason = f.reason;
  if (f.overdue_only) p.overdue_only = true;
  return p;
}

function exportExcel(rows, reasonLabel) {
  const cols = [...PSO_COLUMNS, ...EXPORT_EXTRA];
  const cell = (r, c) => {
    if (c.field === "reasons") return (r.reasons || []).map(reasonLabel).join(", ");
    if (c.kind === "number" || c.kind === "money") return Number(r[c.field] || 0);
    if (c.kind === "date") return fmtDate(r[c.field]);
    return r[c.field] ?? "";
  };
  const ws = XLSX.utils.aoa_to_sheet([cols.map((c) => c.header), ...rows.map((r) => cols.map((c) => cell(r, c)))]);
  ws["!cols"] = cols.map((c) => ({ wch: ["part_name", "reasons", "customer_name"].includes(c.field) ? 32 : 14 }));

  // Customer-wise roll-up of the same rows.
  const byCust = new Map();
  for (const r of rows) {
    const k = `${r.customer_name}|${r.currency}`;
    const e = byCust.get(k) || { customer: r.customer_name, currency: r.currency, orders: new Set(), lines: 0, balance: 0, value: 0, overdue: 0 };
    e.orders.add(r.cso_id); e.lines += 1; e.balance += Number(r.balance_qty || 0);
    e.value += Number(r.pending_value || 0); e.overdue += r.overdue ? 1 : 0;
    byCust.set(k, e);
  }
  const custRows = [...byCust.values()].sort((a, b) => b.value - a.value);
  const ws2 = XLSX.utils.aoa_to_sheet([
    ["Customer", "Currency", "Sales Orders", "Pending Lines", "Balance Qty", "Pending Value", "Overdue Lines"],
    ...custRows.map((e) => [e.customer, e.currency, e.orders.size, e.lines, Math.round(e.balance * 1e4) / 1e4, Math.round(e.value * 100) / 100, e.overdue]),
  ]);
  ws2["!cols"] = [{ wch: 36 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 14 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Pending Lines");
  XLSX.utils.book_append_sheet(wb, ws2, "By Customer");
  XLSX.writeFile(wb, `pending_sales_order_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function SortHeader({ col, sortBy, sortDir, onSort }) {
  const numeric = col.kind === "number" || col.kind === "money";
  const sortable = SORTABLE.has(col.field);
  const active = sortBy === col.field;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 whitespace-nowrap ${numeric ? "text-right" : "text-left"}`}>
      {sortable ? (
        <button type="button" onClick={() => onSort(col.field)} className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? "text-slate-900" : ""}`} data-testid={`pso-sort-${col.field}`}>
          {col.header}
          <Icon className={`w-3 h-3 ${active ? "" : "opacity-40"}`} />
        </button>
      ) : col.header}
    </th>
  );
}

// One linked document in the expanded row's trail.
function DocRef({ to, doc, extra }) {
  return (
    <li className="flex items-center gap-2">
      <Link to={to} state={{ selectId: doc.id }} className="mono font-semibold text-blue-700 hover:underline">{doc.code}</Link>
      <Badge className="bg-slate-100 text-slate-600 font-medium border-0 capitalize">{doc.status}</Badge>
      <span className="text-slate-600">qty {fmtQty(doc.qty)}</span>
      {extra && <span className="text-slate-400">{extra}</span>}
    </li>
  );
}

function DocTrail({ row }) {
  const groups = [
    { title: "Packing Slips", to: "/packing-slips", docs: row.packing_slips, extra: (d) => (d.dc_code ? `→ ${d.dc_code}` : "") },
    { title: "Delivery Challans", to: "/delivery-challans", docs: row.dcs, extra: (d) => (d.dispatch_date ? `dispatched ${fmtDate(d.dispatch_date)}` : d.dc_date ? `raised ${fmtDate(d.dc_date)}` : "") },
    { title: "Tax Invoices", to: "/tax-invoices", docs: row.invoices, extra: (d) => (d.dc_code ? `on ${d.dc_code}` : "") },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs" data-testid={`pso-trail-${row.id}`}>
      <div className="space-y-1 text-slate-600">
        <div className="font-semibold text-slate-800">Order</div>
        <div>TO: <span className="mono">{row.to_code || "—"}</span></div>
        <div>Customer PO: {row.po_reference || "—"}</div>
        <div>Sales person: {row.sales_person || "—"}</div>
        <div>Net rate: {formatMoney(row.rate, row.currency)} / {row.uom}</div>
        <div>Not on any DC: {fmtQty(row.not_on_dc_qty)} · DC qty not invoiced: {fmtQty(row.uninvoiced_qty)}</div>
        {row.stock_can_cover && <div className="text-emerald-700 font-medium">FG stock can cover the unpacked balance</div>}
        {row.under_revision && <div className="text-amber-700 font-medium">Sales Order is under revision</div>}
      </div>
      {groups.map((g) => (
        <div key={g.title}>
          <div className="font-semibold text-slate-800 mb-1">{g.title}</div>
          {g.docs?.length ? (
            <ul className="space-y-1">
              {g.docs.map((d) => <DocRef key={d.id} to={g.to} doc={d} extra={g.extra(d)} />)}
            </ul>
          ) : <div className="text-slate-400">None yet</div>}
        </div>
      ))}
    </div>
  );
}

export default function PendingSalesOrderReport() {
  const [form, setForm] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [sortBy, setSortBy] = useState("delivery_date");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [tick, setTick] = useState(0);

  // Keep the report in step with the Sales Order / DC / Invoice screens.
  useEffect(() => {
    const refresh = () => setTick((t) => t + 1);
    const id = setInterval(refresh, REFRESH_MS);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(id); window.removeEventListener("focus", refresh); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get("/reports/pending-sales-orders", {
      params: { ...toParams(applied), sort_by: sortBy, sort_dir: sortDir, page, page_size: pageSize },
    })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(formatApiError(err?.response?.data?.detail)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [applied, sortBy, sortDir, page, pageSize, tick]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const runSearch = (e) => {
    e?.preventDefault();
    if (form.date_from && form.date_to && form.date_from > form.date_to) {
      toast.error("SO Date From must be on or before SO Date To");
      return;
    }
    if (form.due_from && form.due_to && form.due_from > form.due_to) {
      toast.error("Due Date From must be on or before Due Date To");
      return;
    }
    setPage(1);
    setApplied({ ...form });
  };

  const reset = () => {
    setForm(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
    setSortBy("delivery_date");
    setSortDir("asc");
  };

  // Clicking a stage chip filters to that stage straight away (again to clear).
  const pickStage = (stage) => {
    const next = { ...applied, stage: applied.stage === stage ? "all" : stage };
    setForm((f) => ({ ...f, stage: next.stage }));
    setApplied(next);
    setPage(1);
  };

  const onSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir(["so_date", "age_days", "balance_qty", "pending_value", "ordered_qty", "dispatched_qty"].includes(field) ? "desc" : "asc");
    }
    setPage(1);
  };

  const toggle = (id) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const refreshNow = useCallback(() => setTick((t) => t + 1), []);

  const reasonList = useMemo(() => data?.reasons || [], [data]);
  const reasonLabel = useCallback((k) => reasonList.find((x) => x.key === k)?.label || k, [reasonList]);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/pending-sales-orders", {
        params: { ...toParams(applied), sort_by: sortBy, sort_dir: sortDir, page_size: 0 },
      });
      const all = res.data.rows || [];
      if (!all.length) {
        toast.error("No rows to export — adjust or clear the filters first.");
        return;
      }
      exportExcel(all, reasonLabel);
      toast.success(`Exported ${all.length} rows to Excel`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const rows = useMemo(() => data?.rows || [], [data]);
  const total = data?.total_rows || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const stages = data?.stages || DEFAULT_STAGES;
  const customers = data?.customers || [];
  const salesPeople = data?.sales_people || [];
  const byStage = data?.summary?.by_stage || {};

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const renderCell = (row, col) => {
    const v = row[col.field];
    switch (col.field) {
      case "so_code":
        return (
          <span className="inline-flex items-center gap-1">
            <button type="button" onClick={() => toggle(row.id)} className="text-slate-400 hover:text-slate-700" aria-label="Show document trail" data-testid={`pso-expand-${row.id}`}>
              {expanded.has(row.id) ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
            <Link to="/customer-sales-orders" state={{ selectId: row.cso_id }} className="mono font-semibold text-blue-700 hover:underline" data-testid={`pso-so-link-${row.id}`}>
              {v}
            </Link>
          </span>
        );
      case "so_date":
        return v ? fmtDate(v) : <span className="text-slate-300">—</span>;
      case "delivery_date":
        if (!v) return <span className="text-slate-300">—</span>;
        return (
          <span className={row.overdue ? "text-red-700 font-semibold" : ""}>
            {fmtDate(v)}
            {row.overdue && <span className="block text-[10px] font-medium">{row.overdue_days}d overdue</span>}
          </span>
        );
      case "part_name":
        return <span className="block max-w-[16rem] whitespace-normal">{v || "—"}</span>;
      case "balance_qty":
        return <span className="font-semibold text-slate-900">{fmtQty(v)} <span className="text-[10px] text-slate-400">{row.uom}</span></span>;
      case "dispatched_qty":
        return (
          <span>
            {fmtQty(v)}
            {Number(v) > 0 && <span className="block text-[10px] text-slate-400">{row.dispatch_pct}%</span>}
          </span>
        );
      case "fg_stock_qty":
        return <span className={row.stock_can_cover ? "text-emerald-700 font-semibold" : ""}>{fmtQty(v)}</span>;
      case "pending_value":
        return formatMoney(v, row.currency);
      case "stage":
        return <Badge className={`${STAGE_TONE[v] || "bg-slate-100 text-slate-700"} font-medium border-0 whitespace-nowrap`}>{v}</Badge>;
      case "reasons":
        return (
          <span className="flex flex-wrap gap-1 max-w-[15rem]">
            {(v || []).map((k) => (
              <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded ${k === "overdue" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>{reasonLabel(k)}</span>
            ))}
          </span>
        );
      default:
        if (col.kind === "number") return fmtQty(v);
        return v || <span className="text-slate-300">—</span>;
    }
  };

  return (
    <div className="space-y-5" data-testid="pending-so-report-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Reports</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Truck className="w-7 h-7 text-blue-600" /> Pending Sales Order Report
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every Sales Order line not yet fully dispatched or invoiced — built live from Sales Orders, Packing Slips,
            Delivery Challans and Tax Invoices.
          </p>
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-2">
          {data?.generated_at && <span data-testid="pso-updated">Updated {new Date(data.generated_at).toLocaleTimeString("en-IN")}</span>}
          <Button type="button" size="sm" variant="outline" onClick={refreshNow} disabled={loading} data-testid="pso-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={runSearch} className="bg-white border border-slate-200 rounded-lg p-4" data-testid="pso-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label htmlFor="pso-date-from" className="text-xs text-slate-600">SO Date From</Label>
            <Input id="pso-date-from" type="date" value={form.date_from} onChange={(e) => set("date_from")(e.target.value)} className="h-9 mt-1" data-testid="pso-date-from" />
          </div>
          <div>
            <Label htmlFor="pso-date-to" className="text-xs text-slate-600">SO Date To</Label>
            <Input id="pso-date-to" type="date" value={form.date_to} onChange={(e) => set("date_to")(e.target.value)} className="h-9 mt-1" data-testid="pso-date-to" />
          </div>
          <div>
            <Label htmlFor="pso-due-from" className="text-xs text-slate-600">Due Date From</Label>
            <Input id="pso-due-from" type="date" value={form.due_from} onChange={(e) => set("due_from")(e.target.value)} className="h-9 mt-1" data-testid="pso-due-from" />
          </div>
          <div>
            <Label htmlFor="pso-due-to" className="text-xs text-slate-600">Due Date To</Label>
            <Input id="pso-due-to" type="date" value={form.due_to} onChange={(e) => set("due_to")(e.target.value)} className="h-9 mt-1" data-testid="pso-due-to" />
          </div>
          <div>
            <Label htmlFor="pso-customer" className="text-xs text-slate-600">Customer</Label>
            <Input id="pso-customer" list="pso-customer-options" value={form.customer} onChange={(e) => set("customer")(e.target.value)} placeholder="Customer name" className="h-9 mt-1" data-testid="pso-customer" />
            <datalist id="pso-customer-options">
              {customers.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <Label htmlFor="pso-so-no" className="text-xs text-slate-600">SO / TO Number</Label>
            <Input id="pso-so-no" value={form.so_no} onChange={(e) => set("so_no")(e.target.value)} placeholder="e.g. SO-9001" className="h-9 mt-1" data-testid="pso-so-no" />
          </div>
          <div>
            <Label htmlFor="pso-item" className="text-xs text-slate-600">Item</Label>
            <Input id="pso-item" value={form.item} onChange={(e) => set("item")(e.target.value)} placeholder="Part no. or description" className="h-9 mt-1" data-testid="pso-item" />
          </div>
          <div>
            <Label htmlFor="pso-sales-person" className="text-xs text-slate-600">Sales Person</Label>
            <Input id="pso-sales-person" list="pso-sales-person-options" value={form.sales_person} onChange={(e) => set("sales_person")(e.target.value)} placeholder="Raised by" className="h-9 mt-1" data-testid="pso-sales-person" />
            <datalist id="pso-sales-person-options">
              {salesPeople.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Stage</Label>
            <Select value={form.stage} onValueChange={set("stage")}>
              <SelectTrigger className="h-9 mt-1" data-testid="pso-stage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stages</SelectItem>
                {stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Pending Reason</Label>
            <Select value={form.reason} onValueChange={set("reason")}>
              <SelectTrigger className="h-9 mt-1" data-testid="pso-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any reason</SelectItem>
                {reasonList.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="pso-search" className="text-xs text-slate-600">Search</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="pso-search" value={form.search} onChange={(e) => set("search")(e.target.value)} placeholder="Any SO / TO / PO / DC / invoice / packing slip no., customer or item" className="h-9 pl-8" data-testid="pso-search" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={form.overdue_only} onChange={(e) => set("overdue_only")(e.target.checked)} className="w-4 h-4 accent-blue-600" data-testid="pso-overdue-only" />
            Overdue only
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="pso-search-btn">
              <Search className="w-3.5 h-3.5 mr-1.5" /> Search
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={reset} data-testid="pso-reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={doExport} disabled={exporting} data-testid="pso-export-excel">
              {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}
              Export to Excel
            </Button>
          </div>
        </div>
      </form>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="pso-summary">
        {SUMMARY.map(({ key, label, icon: Icon, tone, money }) => (
          <div key={key} className="kpi-card !p-4" data-testid={`pso-summary-${key}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 leading-tight">{label}</div>
                <div className={`mt-1.5 font-extrabold text-slate-900 ${money ? "text-lg break-words" : "text-2xl"}`} style={{ fontFamily: "Manrope" }}>
                  {money ? formatCurrencyTotals(data?.summary?.[key] || {}) : fmtQty(data?.summary?.[key] ?? 0)}
                </div>
              </div>
              <div className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center ${tone}`}>
                <Icon className="w-4 h-4" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stage pipeline — counts reflect the current filters; click to filter. */}
      <div className="flex flex-wrap gap-2" data-testid="pso-stages">
        {stages.map((s) => {
          const active = applied.stage === s;
          return (
            <button
              key={s} type="button" onClick={() => pickStage(s)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${active ? "border-blue-600 ring-1 ring-blue-600" : "border-transparent"} ${STAGE_TONE[s] || "bg-slate-100 text-slate-700"}`}
              data-testid={`pso-stage-chip-${DEFAULT_STAGES.indexOf(s)}`}
            >
              {s} <span className="font-bold ml-1">{byStage[s] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2">{error}</div>
      )}

      <div className="relative border border-slate-200 rounded-lg bg-white overflow-x-auto scroll-thin" data-testid="pso-table-wrap">
        {loading && !data && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}
        <table className="min-w-full text-xs" data-testid="pso-table">
          <thead className="bg-slate-50">
            <tr>
              {PSO_COLUMNS.map((col) => (
                <SortHeader key={col.field} col={col} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <Fragment key={row.id}>
                <tr className={`border-b border-slate-100 hover:bg-slate-50/70 align-top ${row.overdue ? "bg-red-50/30" : ""}`} data-testid={`pso-row-${row.id}`}>
                  {PSO_COLUMNS.map((col) => (
                    <td key={col.field} className={`px-3 py-2 whitespace-nowrap text-slate-700 ${col.kind === "number" || col.kind === "money" ? "text-right tabular-nums" : ""}`}>
                      {renderCell(row, col)}
                    </td>
                  ))}
                </tr>
                {expanded.has(row.id) && (
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <td colSpan={PSO_COLUMNS.length} className="px-6 py-3">
                      <DocTrail row={row} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={PSO_COLUMNS.length} className="text-center py-16 text-slate-400">
                  No pending Sales Order lines for the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
        <div data-testid="pso-row-count">
          Showing {rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} line{total === 1 ? "" : "s"}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Rows per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-20 h-8 text-xs" data-testid="pso-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page <= 1} onClick={() => setPage(1)} data-testid="pso-page-first">«</Button>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="pso-page-prev">‹</Button>
            <span className="text-xs px-2">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="pso-page-next">›</Button>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="pso-page-last">»</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
